import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { TemplateIcon } from '../utils/iconResolver';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import { fetchWithOrg } from '../utils/api';
import { useTierGating } from '../hooks/useTierGating';
import AgentInstallSuccessModal from '../components/AgentInstallSuccessModal';
import AgentDetailModal from '../components/AgentDetailModal';
import { getWorkerIcon } from '../utils/workerIcons';
import { getAppLogo } from '../utils/usedApps';
import {
  ArrowRight,
  CaretDown,
  CaretLeft,
  CaretRight,
  Check,
  CircleNotch,
  Eye,
  Funnel,
  Info,
  Lightning,
  MagnifyingGlass,
  Microphone,
  Package,
  ShareNetwork,
  Sparkle,
  Star,
  TrendUp,
  X
} from '@phosphor-icons/react';
import { 
  CapabilityProfileBadge,
  VoiceCapabilityBadge, 
  IntegrationBadges, 
  CapabilityModeBadge,
  RequiresWizardIndicator,
  CAPABILITY_PROFILES,
  VOICE_CAPABLE_PROFILES,
  isVoiceCapableProfile
} from '../components/AgentCapabilityBadges';

/* ── Outcome category colors (matching public marketplace) ── */
const OUTCOME_COLORS = {
  'Lead & Enquiries':       { pill: 'rgba(16,185,129,0.12)', text: '#34D399' },
  'Bookings & Scheduling':  { pill: 'rgba(59,130,246,0.12)', text: '#60A5FA' },
  'Quotes & Revenue':       { pill: 'rgba(16,185,129,0.12)', text: '#10B981' },
  'Payments & Admin':       { pill: 'rgba(250,204,21,0.12)',  text: '#FACC15' },
  'Reviews & Reputation':   { pill: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  'Customer Retention':     { pill: 'rgba(139,92,246,0.12)',  text: '#A78BFA' },
  'Operations':             { pill: 'rgba(6,182,212,0.12)',   text: '#22D3EE' },
  'Advanced':               { pill: 'rgba(100,116,139,0.12)', text: '#94A3B8' },
  'Customer Support':       { pill: 'rgba(6,182,212,0.12)',   text: '#22D3EE' },
  'Sales & Marketing':      { pill: 'rgba(16,185,129,0.12)', text: '#34D399' },
  'HR & Recruiting':        { pill: 'rgba(139,92,246,0.12)', text: '#A78BFA' },
  'Finance & Accounting':   { pill: 'rgba(250,204,21,0.12)', text: '#FACC15' },
  'IT & Development':       { pill: 'rgba(59,130,246,0.12)', text: '#60A5FA' },
  'Legal & Compliance':     { pill: 'rgba(100,116,139,0.12)', text: '#94A3B8' },
  'Content & Creative':     { pill: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
};

const AgentMarketplaceV2 = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const { userPlan, planLimits } = usePlan();
  const { isBasic } = useTierGating();

  // State from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategories, setSelectedCategories] = useState(
    searchParams.get('category')?.split(',').filter(Boolean) || []
  );
  const [selectedIndustries, setSelectedIndustries] = useState(
    searchParams.get('industry')?.split(',').filter(Boolean) || []
  );
  const [selectedComplexity, setSelectedComplexity] = useState(searchParams.get('complexity') || '');
  const [voiceOnly, setVoiceOnly] = useState(searchParams.get('voice') === 'true');
  const [selectedBadges, setSelectedBadges] = useState(
    searchParams.get('badges')?.split(',').filter(Boolean) || []
  );
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recommended');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  
  // V1.2: New capability filters
  const [selectedCapabilities, setSelectedCapabilities] = useState(
    searchParams.get('capability')?.split(',').filter(Boolean) || []
  );
  const [selectedInputTypes, setSelectedInputTypes] = useState(
    searchParams.get('inputs')?.split(',').filter(Boolean) || []
  );
  const [selectedIntegrations, setSelectedIntegrations] = useState(
    searchParams.get('integrations')?.split(',').filter(Boolean) || []
  );

  // Data state
  const [templates, setTemplates] = useState([]);
  const [popularTemplates, setPopularTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [installedAgent, setInstalledAgent] = useState(null);
  
  // Detail modal state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const itemsPerPage = 24;

  // Funnel options
  const categories = [
    'Customer Support', 'Sales & Marketing', 'HR & Recruiting', 
    'Finance & Accounting', 'Operations', 'IT & Development',
    'Legal & Compliance', 'Content & Creative'
  ];

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Retail',
    'Manufacturing', 'Education', 'Real Estate', 'Professional Services'
  ];

  const complexityLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const badges = [
    { value: 'popular', label: 'Popular', icon: TrendUp },
    { value: 'new', label: 'New', icon: Sparkle },
    { value: 'featured', label: 'Featured', icon: Star }
  ];

  // V2.0 PUBLIC BETA: Capability Profile filter options (Source of Truth)
  const capabilityProfileOptions = [
    { value: 'text_agent', label: '🧠 Text Agent', description: 'App-like, instant install', color: 'gray' },
    { value: 'live_voice_s2s_agent', label: '🎙️ Live Voice (S2S)', description: 'Real-time conversational', color: 'purple' },
    { value: 'voice_output_tts_agent', label: '🔊 Voice Output (TTS)', description: 'Speaks responses', color: 'blue' },
    { value: 'voice_input_agent', label: '🗣️ Voice Input', description: 'Consumes audio', color: 'teal' },
    { value: 'integration_automation_agent', label: '⚙️ Integration/Automation', description: 'Requires integrations', color: 'amber' }
  ];

  // Voice-ready only filter: Show agents that have any voice capability
  const voiceReadyOnlyProfiles = ['live_voice_s2s_agent', 'voice_output_tts_agent', 'voice_input_agent'];

  // V1.2: Capability mode options - RENAMED for clarity
  // "How this agent runs" replaces "Agent Type"
  const capabilityModes = [
    { value: 'text_only', label: 'Runs instantly', color: 'blue', description: 'No setup required' },
    { value: 'automation_ready', label: 'Automation-ready', color: 'green', description: 'Has webhook/action support' },
    { value: 'integration_dependent', label: 'Requires integration', color: 'amber', description: 'Needs external tools' }
  ];

  // V2.1: Voice capability filter options (consolidated)
  const voiceFilterOptions = [
    { value: 'voice_capable', label: 'Voice-capable', icon: '🎙️', description: 'Any voice functionality' }
  ];

  // V1.2: Input type options
  // PUBLIC BETA: Canonical input types that can be run today
  // These match actual agent accepted_inputs in the marketplace
  const inputTypeOptions = [
    { value: 'text', label: 'Text', icon: '📝' },
    { value: 'file_pdf', label: 'PDF', icon: '📄' },
    { value: 'file_image', label: 'Images', icon: '🖼️' },
    { value: 'file_docx', label: 'Word Docs', icon: '📝' },
    { value: 'file_xlsx', label: 'Excel', icon: '📊' },
    { value: 'file_csv', label: 'CSV', icon: '📋' },
    { value: 'audio', label: 'Audio', icon: '🎵' },
    { value: 'video', label: 'Video', icon: '🎥' },
    { value: 'voice', label: 'Voice', icon: '🎙️' }
  ];

  // COMING SOON: Integration-dependent input types (post-beta)
  // These require native integrations that aren't available in public beta
  const comingSoonInputTypes = [
    { value: 'email', label: 'Email', icon: '📧', tooltip: 'Available post-beta via native integrations' },
    { value: 'calendar_event', label: 'Calendar', icon: '📅', tooltip: 'Available post-beta via native integrations' },
    { value: 'crm_record', label: 'CRM Records', icon: '👤', tooltip: 'Available post-beta via native integrations' }
  ];

  // V1.2: Integration options (only external system integrations)
  // Note: Webhook is not a "required integration" - it's a capability covered by "Automation Ready"
  const integrationOptions = [
    { value: 'twilio_or_telnyx', label: 'Telephony (Twilio/Telnyx)', icon: '📞' },
    { value: 'xero', label: 'Xero', icon: '💰' },
    { value: 'salesforce', label: 'Salesforce', icon: '☁️' },
    { value: 'microsoft365', label: 'Microsoft 365', icon: '⚙️' },
    { value: 'google_workspace', label: 'Google', icon: '⚙️' }
  ];

  // V2.0: Capability profile filter state (Source of Truth)
  const [selectedCapabilityProfile, setSelectedCapabilityProfile] = useState(
    searchParams.get('capability_profile') || ''
  );
  
  // V2.0: Voice-ready only toggle (convenience filter)
  const [voiceReadyOnly, setVoiceReadyOnly] = useState(
    searchParams.get('voice_ready') === 'true'
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL params whenever filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (selectedCategories.length) params.set('category', selectedCategories.join(','));
    if (selectedIndustries.length) params.set('industry', selectedIndustries.join(','));
    if (selectedComplexity) params.set('complexity', selectedComplexity);
    if (voiceOnly) params.set('voice', 'true');
    if (selectedBadges.length) params.set('badges', selectedBadges.join(','));
    if (sortBy !== 'recommended') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, selectedCategories, selectedIndustries, selectedComplexity, 
      voiceOnly, selectedBadges, sortBy, currentPage, setSearchParams]);

  const hasActiveFilters = useCallback(() => {
    return debouncedSearch || 
           selectedCategories.length > 0 || 
           selectedIndustries.length > 0 || 
           selectedComplexity || 
           voiceOnly || 
           selectedBadges.length > 0 ||
           selectedCapabilities.length > 0 ||
           selectedInputTypes.length > 0 ||
           selectedIntegrations.length > 0;
  }, [debouncedSearch, selectedCategories, selectedIndustries, selectedComplexity, 
      voiceOnly, selectedBadges, selectedCapabilities, selectedInputTypes, selectedIntegrations]);

  // Define fetchTemplates before using it in useEffect
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      
      // When capability/input/integration filters are active, fetch more templates
      // because client-side filtering happens after fetch
      const hasClientSideFilters = selectedCapabilities.length > 0 || 
                                   selectedInputTypes.length > 0 || 
                                   selectedIntegrations.length > 0;
      const fetchLimit = hasClientSideFilters ? 100 : itemsPerPage;
      
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (selectedCategories.length) params.set('category', selectedCategories.join(','));
      if (selectedIndustries.length) params.set('industry', selectedIndustries.join(','));
      if (selectedComplexity) params.set('complexity', selectedComplexity);
      if (voiceOnly) params.set('voice', 'true');
      if (selectedBadges.length) params.set('badges', selectedBadges.join(','));
      params.set('sort', sortBy);
      params.set('page', hasClientSideFilters ? '1' : currentPage.toString());
      params.set('limit', fetchLimit.toString());

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/marketplace?${params.toString()}`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setTotalResults(data.total || 0);
        setTotalPages(data.total_pages || 0);
      }
    } catch (err) {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedCategories, selectedIndustries, selectedComplexity,
      voiceOnly, selectedBadges, sortBy, currentPage, activeOrganization,
      selectedCapabilities, selectedInputTypes, selectedIntegrations]);

  const fetchPopularTemplates = useCallback(async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/popular?limit=5`,
        {},
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setPopularTemplates(data.templates || []);
      }
    } catch (err) {
      setPopularTemplates([]);
    }
  }, [activeOrganization]);

  // Fetch templates when filters change
  useEffect(() => {
    if (activeOrganization) {
      fetchTemplates();
    }
  }, [fetchTemplates, activeOrganization]);

  // Fetch popular templates only on page 1 with no filters
  useEffect(() => {
    if (hasActiveFilters() || currentPage > 1) {
      setPopularTemplates([]);
    } else if (activeOrganization) {
      fetchPopularTemplates();
    }
  }, [fetchPopularTemplates, hasActiveFilters, currentPage, activeOrganization]);

  const handleInstallTemplate = async (templateId, templateName) => {
    // Check plan limits
    if (planLimits?.max_agents === 0) {
      alert('Your current plan does not allow creating agents. Please upgrade to Pro or Partner Pro.');
      return;
    }

    try {
      setInstalling(templateId);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/${templateId}/instantiate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find template name
        const template = templates.find(t => t.id === templateId) || 
                        popularTemplates.find(t => t.id === templateId);
        
        // Phase 4: Auto-redirect to wizard if required (acceptance criteria)
        // Don't show modal - directly navigate to wizard for voice agents
        if (data.requires_wizard) {
          // Basic plan users get the demo instead of full wizard
          if (isBasic) {
            navigate('/voice/demo');
          } else {
            navigate(`/partner-pro/ai-workforce/agents/${data.agent_id}/wizard`);
          }
          return;
        }
        
        // For non-wizard agents, show success modal
        setInstalledAgent({
          id: data.agent_id,
          name: template?.name || 'Agent',
          requiresWizard: false,
          wizardType: '',
          capabilityProfile: data.capability_profile || 'text_agent'
        });
        setShowSuccessModal(true);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to install agent');
      }
    } catch (err) {
      console.error('Error installing template:', err);
      alert('Failed to install agent. Please try again.');
    } finally {
      setInstalling(null);
    }
  };
  
  const handleAssignAccess = () => {
    setShowSuccessModal(false);
    // Navigate to Team Members page
    navigate('/admin/team-members');
  };
  
  const handleGoToAgent = () => {
    setShowSuccessModal(false);
    if (installedAgent) {
      // Navigate to worker detail page for configuration
      navigate(`/workers/${installedAgent.id}/detail`);
    }
  };
  
  // Phase 4: Navigate to wizard for voice/integration agents (voice agents still use specialized wizard)
  const handleGoToWizard = () => {
    setShowSuccessModal(false);
    if (installedAgent) {
      // Basic plan users get the demo instead of full wizard
      if (isBasic) {
        navigate('/voice/demo');
      } else {
        navigate(`/partner-pro/ai-workforce/agents/${installedAgent.id}/wizard`);
      }
    }
  };

  // Open detail modal
  const handleOpenDetail = (template) => {
    setSelectedTemplate(template);
    setShowDetailModal(true);
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedTemplate(null);
  };

  // Install from detail modal
  const handleInstallFromDetail = async (templateId, templateName) => {
    await handleInstallTemplate(templateId, templateName);
    handleCloseDetail();
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedIndustries([]);
    setSelectedComplexity('');
    setVoiceOnly(false);
    setSelectedBadges([]);
    setSelectedCapabilities([]);
    setSelectedInputTypes([]);
    setSelectedIntegrations([]);
    setSortBy('recommended');
    setCurrentPage(1);
  };

  // V1.2: Toggle functions for new filters
  const toggleCapability = (capability) => {
    setSelectedCapabilities(prev =>
      prev.includes(capability)
        ? prev.filter(c => c !== capability)
        : [...prev, capability]
    );
    setCurrentPage(1);
  };

  const toggleInputType = (inputType) => {
    setSelectedInputTypes(prev =>
      prev.includes(inputType)
        ? prev.filter(i => i !== inputType)
        : [...prev, inputType]
    );
    setCurrentPage(1);
  };

  const toggleIntegration = (integration) => {
    setSelectedIntegrations(prev =>
      prev.includes(integration)
        ? prev.filter(i => i !== integration)
        : [...prev, integration]
    );
    setCurrentPage(1);
  };

  // V1.2: Client-side filtering for capability filters
  // INTEGRATION SEMANTICS:
  // - required: Agent NEEDS this integration to function (used for "Needs Integration" badge + filtering)
  // - optional: Agent works better with this but can function without it
  // - compatible: Agent can output to this system but doesn't depend on it
  const filterTemplatesByV12 = (templateList) => {
    return templateList.filter(template => {
      // Capability mode filter (OR within group)
      if (selectedCapabilities.length > 0) {
        const templateMode = template.capability_mode || 'text_only';
        if (!selectedCapabilities.includes(templateMode)) return false;
      }
      
      // Input types filter (OR - show if ANY selected input is supported)
      if (selectedInputTypes.length > 0) {
        const templateInputs = template.input_types || ['text'];
        const hasAnyInput = selectedInputTypes.some(input => templateInputs.includes(input));
        if (!hasAnyInput) return false;
      }
      
      // Integrations filter - ONLY use REQUIRED integrations for filtering
      // Compatible integrations should NOT cause filtering matches
      if (selectedIntegrations.length > 0) {
        const reqs = template.integration_requirements || {};
        const requiredIntegrations = reqs.required || [];
        const hasAnyRequiredIntegration = selectedIntegrations.some(int => 
          requiredIntegrations.some(i => i.toLowerCase() === int.toLowerCase())
        );
        if (!hasAnyRequiredIntegration) return false;
      }
      
      return true;
    });
  };

  // Get filtered templates
  const filteredTemplates = filterTemplatesByV12(templates);
  const displayedTemplates = filteredTemplates;
  const filteredTotal = selectedCapabilities.length > 0 || selectedInputTypes.length > 0 || selectedIntegrations.length > 0
    ? filteredTemplates.length
    : totalResults;

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
    setCurrentPage(1);
  };

  const toggleIndustry = (industry) => {
    setSelectedIndustries(prev =>
      prev.includes(industry)
        ? prev.filter(i => i !== industry)
        : [...prev, industry]
    );
    setCurrentPage(1);
  };

  const toggleBadge = (badge) => {
    setSelectedBadges(prev =>
      prev.includes(badge)
        ? prev.filter(b => b !== badge)
        : [...prev, badge]
    );
    setCurrentPage(1);
  };

  const getBadgeIcon = (template) => {
    if (template.popularity_score >= 80) {
      return <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded">POPULAR</span>;
    }
    if (template.is_featured) {
      return <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">FEATURED</span>;
    }
    const daysSinceCreation = (new Date() - new Date(template.created_at)) / (1000 * 60 * 60 * 24);
    if (daysSinceCreation < 14) {
      return <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded">NEW</span>;
    }
    return null;
  };

  return (
    <div className={embedded ? "max-w-[1280px] mx-auto" : "min-h-screen bg-[#0A0A0A]"}>
      {/* Header */}
      <div className={embedded
        ? "mb-6"
        : "border-b border-[rgba(255,255,255,0.1)] bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-20"
      }>
        <div className={embedded ? "" : "max-w-7xl mx-auto px-6 py-6"}>
          <div className="flex items-center justify-between mb-6">
            <div>
              {embedded && (
                <button
                  onClick={() => navigate('/workers')}
                  className="flex items-center gap-1 text-sm text-[#7D8896] hover:text-[#A0AEC0] mb-3 transition-colors"
                >
                  <CaretLeft size={14} />
                  Back to Workers
                </button>
              )}
              <h1 className={`${embedded ? 'text-2xl' : 'text-3xl'} font-bold text-white mb-2`}>
                {embedded ? 'Browse Worker Templates' : 'Your AI Workforce, designed to collaborate'}
              </h1>
              <p className="text-gray-400 text-sm">
                {embedded
                  ? 'Install pre-built workers to get started instantly.'
                  : 'Install agents that analyse, create, and execute work — some run instantly, others connect to your tools.'}
                {!embedded && (
                  <span className="text-[#00E5C0]"> 3,000+ tools available via Composio.</span>
                )}
              </p>
            </div>
            {!embedded && (
              <Link
                to="/partner-pro/ai-workforce/agents"
                className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)]"
              >
                My Agents
              </Link>
            )}
          </div>

          {/* MagnifyingGlass & Filters Bar */}
          <div className="flex gap-3">
            {/* MagnifyingGlass */}
            <div className="flex-1 relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search agents by name, use case, or capability..."
                className="w-full pl-12 pr-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00E5C0]"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Funnel Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-6 py-3 rounded-lg border flex items-center gap-2 ${
                hasActiveFilters()
                  ? 'bg-[#6366F1] border-[#6366F1] text-white'
                  : 'glass border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.1)]'
              }`}
            >
              <Funnel size={18} />
              Filters
              {hasActiveFilters() && (
                <span className="bg-white text-[#6366F1] px-2 py-0.5 rounded-full text-xs font-bold">
                  {[selectedCategories.length, selectedIndustries.length, selectedComplexity ? 1 : 0, 
                    voiceOnly ? 1 : 0, selectedBadges.length, selectedCapabilities.length,
                    selectedInputTypes.length, selectedIntegrations.length].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-3 glass border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            >
              <option value="recommended">Recommended</option>
              <option value="popular">Most Popular</option>
              <option value="newest">Newest</option>
              <option value="a-z">A to Z</option>
            </select>
          </div>

          {/* Funnel Panel */}
          {showFilters && (
            <div className="mt-4 p-4 glass rounded-lg border border-[rgba(255,255,255,0.1)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                
                {/* 🔧 How this agent runs */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-sm font-semibold text-white">How this agent runs</h3>
                    <div className="relative group/tooltip">
                      <Info size={12} className="text-gray-500 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                        This describes how the agent runs, not what it does.
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">How the agent executes work once installed.</p>
                  <div className="space-y-2">
                    {/* Runs instantly */}
                    <label className="flex items-start gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedCapabilities.includes('text_only')}
                        onChange={() => toggleCapability('text_only')}
                        className="rounded mt-0.5"
                      />
                      <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                      <div>
                        <span className="text-sm text-gray-300 group-hover:text-white block">Runs instantly</span>
                        <span className="text-xs text-gray-500">No setup required</span>
                      </div>
                    </label>
                    {/* Requires integration */}
                    <label className="flex items-start gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedCapabilities.includes('integration_dependent')}
                        onChange={() => toggleCapability('integration_dependent')}
                        className="rounded mt-0.5"
                      />
                      <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5" />
                      <div>
                        <span className="text-sm text-gray-300 group-hover:text-white block">Requires integration</span>
                        <span className="text-xs text-gray-500">Connect tools before running</span>
                      </div>
                    </label>
                    {/* Voice-capable */}
                    <label className="flex items-start gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={voiceOnly}
                        onChange={(e) => {
                          setVoiceOnly(e.target.checked);
                          setCurrentPage(1);
                        }}
                        className="rounded mt-0.5"
                      />
                      <span className="text-sm mt-0.5">🎙️</span>
                      <div>
                        <span className="text-sm text-gray-300 group-hover:text-white block">Voice-capable</span>
                        <span className="text-xs text-gray-500">Uses microphone or telephony</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* 🔗 Connects to */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-sm font-semibold text-white">Connects to</h3>
                    <div className="relative group/tooltip">
                      <Info size={12} className="text-gray-500 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                        These tools must be connected before the agent can run.
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">External tools this agent can work with.</p>
                  <div className="space-y-2">
                    {integrationOptions.map(int => (
                      <label key={int.value} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={selectedIntegrations.includes(int.value)}
                          onChange={() => toggleIntegration(int.value)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-300 group-hover:text-white">{int.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* 🧭 Use case */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-sm font-semibold text-white">Use case</h3>
                    <div className="relative group/tooltip">
                      <Info size={12} className="text-gray-500 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                        Agents can span multiple use cases.
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Where this agent is typically used.</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {categories.map(category => (
                      <label key={category} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategory(category)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-300 group-hover:text-white">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* ⭐ More options */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <h3 className="text-sm font-semibold text-white">More options</h3>
                    <div className="relative group/tooltip">
                      <Info size={12} className="text-gray-500 cursor-help" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                        Curated by usage and recency.
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 mt-2">
                    {badges.map(badge => {
                      const Icon = badge.icon;
                      return (
                        <label key={badge.value} className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={selectedBadges.includes(badge.value)}
                            onChange={() => toggleBadge(badge.value)}
                            className="rounded"
                          />
                          <Icon size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-300 group-hover:text-white">{badge.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 📥 Advanced filters (collapsible) */}
              <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  {showAdvancedFilters ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  Advanced filters
                  {selectedInputTypes.length > 0 && (
                    <span className="bg-[#6366F1] text-white px-1.5 py-0.5 rounded-full text-xs">
                      {selectedInputTypes.length}
                    </span>
                  )}
                </button>
                
                {showAdvancedFilters && (
                  <div className="mt-3 pl-4">
                    <p className="text-xs text-gray-500 mb-3">Refine by input formats, systems, or use case.</p>
                    
                    {/* 📄 Input types - Available in Public Beta */}
                    <div className="flex items-center gap-1 mb-1">
                      <h4 className="text-xs font-semibold text-gray-400 uppercase">Input types</h4>
                      <div className="relative group/tooltip">
                        <Info size={10} className="text-gray-600 cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity pointer-events-none z-10">
                          Some agents support multiple input types.
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">What the agent accepts as input.</p>
                    <p className="text-xs text-[#6366F1]/70 mb-2 italic">Filters match what you can run in Public Beta.</p>
                    <div className="flex flex-wrap gap-2">
                      {inputTypeOptions.map(input => (
                        <label 
                          key={input.value} 
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors ${
                            selectedInputTypes.includes(input.value)
                              ? 'bg-[#6366F1] text-white'
                              : 'bg-[rgba(255,255,255,0.05)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)]'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedInputTypes.includes(input.value)}
                            onChange={() => toggleInputType(input.value)}
                            className="sr-only"
                          />
                          <span className="text-sm">{input.icon}</span>
                          <span className="text-xs">{input.label}</span>
                        </label>
                      ))}
                    </div>
                    
                    {/* Coming Soon - Integration-dependent inputs */}
                    <div className="mt-4 pt-3 border-t border-[rgba(255,255,255,0.05)]">
                      <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Coming soon (integrations)</p>
                      <div className="flex flex-wrap gap-2">
                        {comingSoonInputTypes.map(input => (
                          <div 
                            key={input.value}
                            className="relative group/coming"
                          >
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[rgba(255,255,255,0.02)] text-gray-600 cursor-not-allowed opacity-60">
                              <span className="text-sm">{input.icon}</span>
                              <span className="text-xs">{input.label}</span>
                            </div>
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-xs text-gray-300 rounded whitespace-nowrap opacity-0 group-hover/coming:opacity-100 transition-opacity pointer-events-none z-10">
                              {input.tooltip}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Clear Filters */}
              {hasActiveFilters() && (
                <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-[#6366F1] hover:text-[#8B5CF6] flex items-center gap-2"
                  >
                    <X size={16} />
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={embedded ? "py-4" : "max-w-7xl mx-auto px-6 py-8"}>
        <div className="flex gap-6">
          {/* Main Grid */}
          <div className="flex-1">
            {/* Results Count */}
            {!loading && (
              <div className="mb-4 text-sm text-gray-400">
                {filteredTotal} {filteredTotal === 1 ? 'agent' : 'agents'} found
              </div>
            )}

            {/* Loading Skeleton */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="glass rounded-xl border border-[rgba(255,255,255,0.1)] p-6 animate-pulse">
                    <div className="w-12 h-12 bg-gray-700 rounded-xl mb-4"></div>
                    <div className="h-5 bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                  </div>
                ))}
              </div>
            ) : templates.length === 0 ? (
              /* Empty State */
              <div className="text-center py-16">
                <Package size={64} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No agents found</h3>
                <p className="text-gray-400 mb-6">Try adjusting your filters or search query</p>
                {hasActiveFilters() && (
                  <button
                    onClick={clearAllFilters}
                    className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              /* Template Grid */
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {displayedTemplates.map(template => {
                    const capabilityProfile = template.capability_profile || 'text_agent';
                    const isVoice = isVoiceCapableProfile(capabilityProfile);
                    const tools = template.works_with || [];
                    const category = template.outcome_category || template.category || 'Operations';
                    const color = OUTCOME_COLORS[category] || OUTCOME_COLORS['Operations'] || { pill: 'rgba(100,116,139,0.12)', text: '#94A3B8' };

                    return (
                      <div
                        key={template.id}
                        data-testid={`template-card-${template.id}`}
                        onClick={() => handleOpenDetail(template)}
                        className="group rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 flex flex-col cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] hover:border-[rgba(255,255,255,0.1)]"
                      >
                        {/* Header: Icon + Name */}
                        <div className="flex items-start gap-3 mb-4">
                          <img
                            src={getWorkerIcon(category, template.name)}
                            alt=""
                            className="w-11 h-11 rounded-xl object-cover shrink-0"
                            loading="lazy"
                            data-testid={`worker-icon-${template.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-[#F0F4F8] font-semibold text-[15px] leading-snug group-hover:text-[#00E5C0] transition-colors line-clamp-2">
                              {template.name}
                            </h3>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-[#7D8896] text-[13px] leading-relaxed line-clamp-2 mb-4">
                          {template.short_description || template.description}
                        </p>

                        {/* Tool logos row */}
                        {tools.length > 0 && (
                          <div className="flex items-center gap-1.5 mb-4 overflow-hidden" data-testid={`card-tools-${template.id}`}>
                            {tools.slice(0, 5).map((name, i) => (
                              <div key={i} className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0" title={name}>
                                <img src={getAppLogo(name)} alt={name} className="w-4 h-4 rounded object-contain" loading="lazy"
                                  onError={e => { e.target.src = '/app-icons/generic-app.svg'; e.target.onerror = null; }} />
                              </div>
                            ))}
                            {tools.length > 5 && (
                              <span className="text-[10px] text-[#7D8896] shrink-0 ml-0.5">+{tools.length - 5}</span>
                            )}
                          </div>
                        )}

                        {/* Spacer */}
                        <div className="mt-auto" />

                        {/* Footer: Category badge + View */}
                        <div className="flex items-center justify-between pt-4 border-t border-[rgba(255,255,255,0.04)]">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full truncate max-w-[160px]"
                              style={{ backgroundColor: color.pill, color: color.text }}
                              data-testid={`template-tag-${template.id}`}
                            >
                              {category}
                            </span>
                            {isVoice && (
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[rgba(139,92,246,0.12)] text-[#A78BFA]">
                                Voice
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] font-medium text-[#00E5C0] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            View <ArrowRight size={10} />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 glass rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(255,255,255,0.1)]"
                    >
                      Previous
                    </button>
                    
                    <div className="flex gap-2">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-10 h-10 rounded-lg ${
                              currentPage === pageNum
                                ? 'bg-[#6366F1] text-white'
                                : 'glass text-gray-300 hover:bg-[rgba(255,255,255,0.1)]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 glass rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(255,255,255,0.1)]"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Popular Rail */}
          {!hasActiveFilters() && currentPage === 1 && popularTemplates.length > 0 && (
            <div className="w-80 shrink-0">
              <div className="sticky top-32">
                <div className="glass rounded-xl border border-[rgba(255,255,255,0.1)] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendUp size={18} className="text-[#00E5C0]" />
                    <h3 className="text-lg font-semibold text-white">Popular Installs</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {popularTemplates.map(template => (
                      <div
                        key={template.id}
                        className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors cursor-pointer group"
                        onClick={() => handleOpenDetail(template)}
                      >
                        <div className="flex items-start gap-3">
                          <img src={getWorkerIcon(template.outcome_category || template.category, template.name)} alt=""
                            className="w-8 h-8 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-white mb-1 group-hover:text-[#00E5C0] transition-colors">{template.name}</h4>
                            <p className="text-xs text-gray-400 line-clamp-2">{template.short_description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Success Modal with Privacy-First Message - Phase 4 Updated */}
      <AgentInstallSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        agentName={installedAgent?.name}
        agentId={installedAgent?.id}
        requiresWizard={installedAgent?.requiresWizard}
        wizardType={installedAgent?.wizardType}
        capabilityProfile={installedAgent?.capabilityProfile}
        onAssignAccess={handleAssignAccess}
        onGoToAgent={handleGoToAgent}
        onGoToWizard={handleGoToWizard}
      />
      
      {/* Agent Detail Modal */}
      <AgentDetailModal
        template={selectedTemplate}
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
        onInstall={handleInstallFromDetail}
        installing={installing === selectedTemplate?.id}
      />
    </div>
  );
};

export default AgentMarketplaceV2;
